<template>
  <div class="flex flex-col gap-4 py-4">
    <div class="flex flex-row flex-wrap items-center">
      <h2 class="text-title-large md:text-headline-large flex grow flex-row items-center gap-0.5">
        {{ $t('settings.organization') }} <BaseIcon>chevron_right</BaseIcon>
        {{ $t('settings.members') }}
      </h2>
      <div class="mt-4 flex w-full flex-col gap-2 md:mt-0 md:w-auto md:flex-row">
        <BaseTextButton
          v-if="hasLumia"
          size="M"
          color="tonal"
          icon="person_add"
          @click="openAddLoginLessMemberDialog"
          >{{ $t('organization.loginLessMember.add') }}</BaseTextButton
        >
        <BaseTextButton
          :title="$t('common.invite')"
          size="M"
          color="tonal"
          icon="person_add"
          @click="openAddMemberDialog"
          >{{ $t('common.invite') }}</BaseTextButton
        >
      </div>
    </div>
    <ul v-if="activeMembers.length > 0" class="flex flex-col gap-0.5">
      <li
        v-for="member in activeMembers"
        :key="member.id"
        class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large p-2 pr-1"
      >
        <MemberListItem
          :member="member"
          :availableRoles="organization.availableRoles"
          @edited-roles="(roles) => editedRoles(member.id, roles)"
          @name-updated="(name) => updateLoginLessMember(member.id, name)"
          @deleted="deleteMember(member.id)"
        />
      </li>
    </ul>
    <ul v-if="loginLessMembers.length > 0" class="flex flex-col gap-0.5">
      <li
        v-for="member in loginLessMembers"
        :key="member.id"
        class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large p-2 pr-1"
      >
        <MemberListItem
          :member="member"
          :availableRoles="organization.availableRoles"
          @edited-roles="(roles) => editedRoles(member.id, roles)"
          @name-updated="(name) => updateLoginLessMember(member.id, name)"
          @deleted="deleteMember(member.id)"
        />
      </li>
    </ul>
    <ul v-if="pendingMembers.length > 0" class="flex flex-col gap-0.5">
      <li
        v-for="member in pendingMembers"
        :key="member.id"
        class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large p-2 pr-1"
      >
        <MemberListItem
          :member="member"
          :availableRoles="organization.availableRoles"
          @edited-roles="(roles) => editedRoles(member.id, roles)"
          @name-updated="(name) => updateLoginLessMember(member.id, name)"
          @deleted="deleteMember(member.id)"
        />
      </li>
    </ul>
    <InviteMemberDialog
      :is-open="isAddMemberDialogOpen"
      :available-roles="organization.availableRoles"
      @member-added="addMember"
      @close="closeAddMemberDialog"
    />
    <AddLoginLessMemberDialog
      :is-open="isAddLoginLessMemberDialogOpen"
      @members-added="addLoginLessMembers"
      @close="closeAddLoginLessMemberDialog"
    />
  </div>
</template>
<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import type { ManageableOrganization } from '../composables/useOrganization'
  import { useOrganizationSettings } from '../composables/useOrganizationSettings'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import MemberListItem from './MemberListItem.vue'
  import InviteMemberDialog from './InviteMemberDialog.vue'
  import AddLoginLessMemberDialog from './AddLoginLessMemberDialog.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { ApplicationModule } from '@/core/models/organization'
  import { useApplicationModule } from '@/core/composables/use-application-module'
  import { useOrganizationCapabilitySettings } from '@/modules/settings/composables/use-organization-capability-settings'

  interface Props {
    organization: ManageableOrganization
  }
  const props = defineProps<Props>()

  const editableOrganization = ref<ManageableOrganization>(props.organization)

  const pendingMembers = computed(() => {
    return editableOrganization.value.members.filter((member) => member.pending)
  })

  const activeMembers = computed(() => {
    return editableOrganization.value.members.filter((member) => !member.pending && member.canLogin)
  })

  const loginLessMembers = computed(() => {
    return editableOrganization.value.members.filter(
      (member) => !member.pending && !member.canLogin,
    )
  })

  watch(
    () => props.organization,
    (newOrganization) => {
      editableOrganization.value = newOrganization
    },
    { deep: true },
  )

  const {
    editedRoles,
    deleteMember,
    isAddMemberDialogOpen,
    openAddMemberDialog,
    closeAddMemberDialog,
    addMember,
    addLoginLessMembers,
    updateLoginLessMember,
    isAddLoginLessMemberDialogOpen,
    openAddLoginLessMemberDialog,
    closeAddLoginLessMemberDialog,
  } = useOrganizationSettings(editableOrganization)

  useOrganizationCapabilitySettings()

  const { hasModule } = useApplicationModule()
  const hasLumia = computed(() => hasModule(ApplicationModule.LUMIA))
</script>
